import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useExamContext } from '../../context/ExamContext';
import { fetchChatUsers, fetchChatMessages } from '../../api/chatApi';
import ChatTrigger from './ChatTrigger';
import ChatPanel from './ChatPanel';
import { maxMessageId, mergeChatMessages } from './chatMessages';
import './Chat.css';

const BASE_URL = process.env.REACT_APP_CHAT_API_BASE || '';
const POLL_INTERVAL_MS = 15000;
// Polls fetch only messages after the newest known id; every Nth poll reloads the full history as a safety net
// (covers ids that became visible out of order and any message missed while offline).
const FULL_RESYNC_EVERY_POLLS = 10;

export default function Chat() {
  const ctx = useExamContext();
  let currentUserId = null;
  if(ctx?.role === 'student'){
    currentUserId = ctx?.decoded?.student_user_id;
  }else{
    currentUserId = ctx?.decoded?.user_id;
  }
  const currentUserRole = ctx?.decoded ? (ctx.role ?? ctx.decoded.role) : null;
  const registrationId = ctx?.decoded ? (ctx.registrationId ?? ctx.decoded.registrationId) : null;
  const roomId = ctx?.decoded ? (ctx.room_id ?? ctx.decoded.room_id) : null;


  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seenMessageIds, setSeenMessageIds] = useState(() => new Set());
  const [notification, setNotification] = useState(null);
  const openWithMentionsRef = useRef(false);
  const messagesRef = useRef([]);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    let cancelled = false;
    const base = BASE_URL;
    if (!base) {
      setError('Set REACT_APP_CHAT_API_BASE in .env');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all([
      fetchChatUsers({ id: currentUserId, role: currentUserRole, registrationId: registrationId, roomId: roomId }),
      fetchChatMessages({ roomId: roomId }),
    ])
      .then(([userList, messageList]) => {
        if (!cancelled) {
          setUsers(Array.isArray(userList) ? userList : []);
          setMessages(Array.isArray(messageList) ? messageList : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load chat');
          setUsers([]);
          setMessages([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [currentUserId, currentUserRole]);

  useEffect(() => {
    if (!BASE_URL || currentUserId == null) return;
    let polls = 0;
    const poll = () => {
      polls += 1;
      const full = polls % FULL_RESYNC_EVERY_POLLS === 0 || messagesRef.current.length === 0;
      fetchChatMessages({ roomId: roomId, afterId: full ? 0 : maxMessageId(messagesRef.current) })
        .then((messageList) => {
          const prev = messagesRef.current;
          const prevIds = new Set(prev.map((m) => m.id));
          const newMessages = (messageList || []).filter((m) => !prevIds.has(m.id));
          if (newMessages.length === 0) return;
          const currentUserIdStr = String(currentUserId);
          const mentionedNew = newMessages.filter((m) =>
            (m.mentionedUserIds || []).some((id) => String(id) === currentUserIdStr)
          );
          if (full) setMessages(Array.isArray(messageList) ? messageList : []);
          else setMessages((current) => mergeChatMessages(current, newMessages));
          if (mentionedNew.length > 0) {
            openWithMentionsRef.current = true;
            setIsOpen(true);
            setNotification('You were mentioned – opening chat');
          } else {
            setNotification('New message(s) in chat');
          }
        })
        .catch(() => { });
    };
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [currentUserId, roomId]);

  const unreadCount = messages.filter((m) => !seenMessageIds.has(m.id)).length;
  const currentUserIdStr = currentUserId != null ? String(currentUserId) : '';
  const mentionedUnreadIds = messages
    .filter(
      (m) =>
        (m.mentionedUserIds || []).some((id) => String(id) === currentUserIdStr) &&
        !seenMessageIds.has(m.id)
    )
    .map((m) => m.id);
  const hasMentionedUnread = mentionedUnreadIds.length > 0;

  const handleOpen = useCallback(() => {
    if (isOpen) {
      openWithMentionsRef.current = false;
      ctx?.setActiveWidget?.(null);
    } else {
      openWithMentionsRef.current = hasMentionedUnread;
      ctx?.setActiveWidget?.('chat');
    }
  }, [isOpen, hasMentionedUnread, ctx]);

  // Click outside to close - replacement for backdrop to avoid blocking other actions
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e) => {
      // Check if click is outside chat container and trigger
      const triggerBtn = document.querySelector('.chat-trigger');
      if (chatContainerRef.current && 
          !chatContainerRef.current.contains(e.target) && 
          triggerBtn && !triggerBtn.contains(e.target)) {
        setIsOpen(false);
        ctx?.setActiveWidget?.(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, ctx]);

  useEffect(() => {
    if (ctx?.activeWidget === 'chat') {
      setIsOpen(true);
    } else if (ctx?.activeWidget !== 'chat' && isOpen) {
      setIsOpen(false);
      openWithMentionsRef.current = false;
    }
  }, [ctx?.activeWidget]);

  useEffect(() => {
    if (isOpen) {
      setSeenMessageIds((prev) => {
        const next = new Set(prev);
        messages.forEach((m) => next.add(m.id));
        return next;
      });
    }
  }, [isOpen, messages]);

  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 4000);
    return () => clearTimeout(t);
  }, [notification]);

  // Removed local backdrop click handler in favor of global effect

  return (
    <>
      {notification && (
        <div
          className="chat-notification"
          role="status"
          aria-live="polite"
        >
          {notification}
        </div>
      )}
      <div ref={chatContainerRef}>
        <div className={`chat-wrapper ${isOpen ? 'is-open' : ''}`}>
          <div className="chat-panel-container">
            <ChatPanel
              isOpen={isOpen}
              messages={messages}
              setMessages={setMessages}
              currentUserId={currentUserId}
              users={users}
              highlightMessageIds={openWithMentionsRef.current ? mentionedUnreadIds : []}
              justOpenedWithMentions={openWithMentionsRef.current}
              loading={loading}
              error={error}
            />
          </div>
        </div>
        <ChatTrigger
          onClick={handleOpen}
          isOpen={isOpen}
          badgeCount={unreadCount}
          hasMentions={hasMentionedUnread}
        />
      </div>
    </>
  );
}
